import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { CostBreakupReportProps } from "../types";
import {
  embedChartInJSPDF,
  formatCurrency,
  hexToGrayscale,
  pxToInch,
} from "./helpers";

var generateData = function (tableData) {
  const rows = tableData.rows?.map((row: any) => {
    let object = {};
    tableData?.columns?.forEach((col: any) => {
      const content =
        row?.column_content?.find(
          (content: any) => content?.field === col?.field
        )?.content || "";
      object[col.field] = content;
    });
    return object;
  });
  return rows;
};

const addHeaderTitle = (doc, title, yPos = 12, name = "") => {
  // Text position and dimensions
  // const pageWidth = doc.internal.pageSize.getWidth(); // Get page width
  // const x = (pageWidth - textWidth) / 2; // Calculate the x position to center the text
  const x = 7;
  const y = yPos ? yPos : 12;
  const fontSize = 14;

  // Set the font size to calculate the width and height of the text
  doc.setFontSize(fontSize);

  // Draw text
  doc.setFont(undefined, "bold");
  doc.setTextColor(0, 0, 0); // Text color
  doc.text(title, x, y);

  const textWidth = doc.getTextWidth(title); // Get the width of the text

  if (name) {
    // Render the right-aligned text
    doc.setTextColor(128, 128, 128); // Text color
    doc.text(name, textWidth + 10, y);
    doc.setTextColor(0, 0, 0); // Text color
  }
  doc.setFont(undefined, "normal");
};

const addSubHeader = (doc, title, yPos, fontSize = 12) => {
  // Text position and dimensions
  const x = 7;
  const y = yPos;
  const textHeight = fontSize / 2;

  // Set the font size to calculate the width and height of the text
  doc.setFontSize(fontSize);

  // Draw text
  doc.setTextColor(0, 0, 0); // Text color
  doc.text(title, x, y);
  const textWidth = doc.getTextWidth(title); // Get the text width

  // Draw the underline
  doc.line(x, y + 1, x + textWidth, y + 1); // 1 unit below the text

  return textHeight;
};

const addSubTitle = (doc, title, yPos, fontSize = 10) => {
  // Text position and dimensions
  // const pageWidth = doc.internal.pageSize.getWidth(); // Get page width
  // const textWidth = doc.getTextWidth(title); // Get the width of the text
  // const x = (pageWidth - textWidth) / 2; // Calculate the x position to center the text
  const x = 7;
  const y = yPos;
  let textHeight = fontSize / 2;

  // Set the font size to calculate the width and height of the text
  doc.setFontSize(fontSize);

  // Draw text
  doc.setTextColor(128, 128, 128); // Text color

  const pageWidth = doc.internal.pageSize.getWidth();

  const wrappedText = doc.splitTextToSize(title, pageWidth - 15);

  doc.text(wrappedText, x, y);

  wrappedText?.slice(1)?.forEach((_) => {
    textHeight += fontSize / 2;
  });

  // Draw text
  doc.setTextColor(0, 0, 0); // Text color

  return textHeight;
};

const addTextForFrontPage = (doc, name, value, yPos) => {
  // Set up the page width and font size
  const pageWidth = doc.internal.pageSize.getWidth();
  const fontSize = 10;
  const textHeight = fontSize / 2;

  doc.setFontSize(fontSize);

  // Left-aligned text
  const leftText = name;

  // Right-aligned text (value)
  const rightText = value;

  // X position for the left-aligned text (start from the left)
  const leftX = 7;

  // Measure the width of the right-aligned text
  const rightTextWidth = doc.getTextWidth(rightText);

  // X position for the right-aligned text (start from the right, subtract the text width)
  const rightX = pageWidth - 10 - rightTextWidth;

  // Y position for both (on the same line)
  let yPosition = yPos;

  // Render the left-aligned text
  doc.text(leftText, leftX + 1, yPosition);

  // Render the right-aligned text
  doc.text(rightText, rightX, yPosition);

  return textHeight + 6;
};

const addTotalText = (
  doc,
  name,
  value,
  yPos,
  header_color = "",
  xEndPos = 0
) => {
  // Set up the page width and font size
  const pageWidth = doc.internal.pageSize.getWidth();
  const fontSize = 12;
  const textHeight = fontSize / 2;
  const padding = 3;

  doc.setFontSize(fontSize);

  // Left-aligned text
  const leftText = name;

  // Right-aligned text (value)
  const rightText = value;

  // X position for the left-aligned text (start from the left)
  const leftX = 7;

  // Measure the width of the right-aligned text
  const rightTextWidth = doc.getTextWidth(rightText);

  // X position for the right-aligned text (start from the right, subtract the text width)
  const rightX = (xEndPos || pageWidth) - 10 - rightTextWidth;

  // Y position for both (on the same line)
  let yPosition = yPos;

  if (header_color) {
    // Draw background rectangle
    doc.setFillColor(header_color); // Background color
    doc.rect(
      leftX,
      yPosition - textHeight,
      (xEndPos || doc.internal.pageSize.getWidth()) - 7,
      textHeight + padding,
      "F"
    );

    // Draw text
    if (header_color && hexToGrayscale(header_color) > 150) {
      doc.setTextColor(0, 0, 0); // Text color
    } else {
      doc.setTextColor(255, 255, 255); // Text color
    }
  }

  // Render the left-aligned text
  doc.text(leftText, leftX + 1, yPosition);

  // Render the right-aligned text
  doc.text(rightText, rightX, yPosition);

  doc.line(
    leftX,
    yPosition + 2,
    xEndPos || doc.internal.pageSize.getWidth(),
    yPosition + 2
  ); // Draw the line below the text

  return textHeight + 6;
};

const drawTable = (doc, tableData, startY, hideHeader, label = "") => {
  const totalWidth = tableData.columns.reduce(
    (sum, col) => sum + (col.width || 150),
    0
  );

  let tableHeight = 0;

  // Text position and dimensions
  const x = 7;
  const fontSize = 12;

  // if (summaryHeader) {
  //   [
  //     {
  //       label: `Cost Breakup for: `,
  //       value: `${tableData?.bomDetails?.calculated_cost?.short_description} (${
  //         tableData?.bomDetails?.calculated_cost?.sequence_id ?? ""
  //       }-r${tableData?.bomDetails?.calculated_cost?.version ?? 0})`,
  //     },
  //     {
  //       label: `Total BOM Cost: `,
  //       value: `${tableData?.bomDetails?.calculated_cost?.total_cost?.cost} ${tableData?.bomDetails?.calculated_cost?.total_cost?.currency}`,
  //     },
  //     {
  //       label: `Calculated By: `,
  //       value: `${tableData?.bomDetails?.created_user}`,
  //     },
  //     {
  //       label: `Calculated At: `,
  //       value: `${moment(tableData?.bomDetails?.created_time).format("ll")}`,
  //     },
  //     {
  //       label: `Pricing ID: `,
  //       value: label,
  //     },
  //   ].map((content) => {
  //     // Draw title
  //     doc.setFontSize(fontSize - 1);
  //     doc.setTextColor(128, 128, 128);
  //     doc.text(content.label, x, startY);
  //     const titleLabelWidth = doc.getTextWidth(content.label);

  //     doc.setFontSize(fontSize);
  //     doc.setTextColor(0, 0, 0); // Text color
  //     doc.text(content.value, x + titleLabelWidth, startY);

  //     startY += 7;
  //   });
  // }

  const titleHeight = fontSize / 2;

  // Calculate the starting Y position for the table
  const tableStartY = startY;

  doc.autoTable({
    startY: tableStartY,
    head: hideHeader ? [] : [tableData.columns.map((col) => col.header_name)],
    body: generateData(tableData),
    columns: tableData.columns.map((col) => ({
      dataKey: col.field,
      header: hideHeader ? "" : col.header_name,
      //   width: `${pxToInch(col.width || 150)}pt`
    })),
    // columnDefs: tableData.columns.map((col) => ({
    //   targets: col.field,
    //   width: pxToInch(col.width || 150),
    // })),
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: tableData.header_color,
      textColor: `${
        hexToGrayscale(tableData.header_color) > 150 ? "#000000" : "#FFFFFF"
      }`,
    },
    columnStyles: tableData.columns.reduce((acc, col, index) => {
      acc[col.field] = { cellWidth: pxToInch(col.width || 150) };
      return acc;
    }, {}),

    didDrawCell: (data) => {
      if (data.cell.section === "body" || data.cell.section === "head") {
        const { doc, cell } = data;
        doc.setDrawColor(128, 128, 128); // Set border color to grey (RGB: 128, 128, 128)
        // doc.rect(cell.x, cell.y, cell.width, cell.height, "S");
      }
    },
    didDrawPage: (data) => {
      // This will be called after each page is drawn
      tableHeight = data.cursor.y - tableStartY;

      // if (!doc.internal.pages[data.pageNumber].includes("Generated using IEHUB")) {
      //   const footerText = `Generated using IEHUB on ${moment(new Date()).format(
      //     "ll"
      //   )}`;
      //   const textWidth = doc.getTextWidth(footerText);
      //   doc.setFontSize(10);

      //   doc.setTextColor(125, 125, 125); // Text color
      //   doc.text(footerText, pageWidth - textWidth, pageHeight - 5);
      // }

      // Footer logic
    },
    // Custom cell hook for additional formatting
    didParseCell: function (data) {
      // Identify currency columns
      const currencyColumns = ["unitCost", "qty", "totalCost"];
      if (currencyColumns.includes(data.column.dataKey)) {
        // Right align the cell
        data.cell.styles.halign = "right";
      }
    },
    tableWidth: pxToInch(totalWidth),
    margin: { left: x, right: x },
  });

  // Calculate and return the total height (text + table)
  const totalHeight = tableHeight + Math.max(titleHeight) + 5;
  const lastColWidth =
    doc?.lastAutoTable?.columns?.[doc?.lastAutoTable?.columns?.length - 1]
      ?.width;
  const lastColX =
    doc?.lastAutoTable?.body?.[0]?.cells?.[
      tableData.columns?.[tableData.columns?.length - 1]?.field
    ]?.x;
  return { y: startY + totalHeight, x: lastColWidth + lastColX };
};

function processBOMDetailsFlatten(
  bomDetails,
  level = "1",
  base_currency: string,
  hasNotes: boolean,
  useMBFormatter: boolean,
  normalized: {
    isNormalized: boolean;
    final_price: number;
    total_first_level: number;
  },
  parentQty = 0,
  total_bom_cost = 0
) {
  const flattenedList = [];

  let bom_pricing_cost = 0;

  let bom_unit_price = 0;

  if (level === "1" && normalized.isNormalized) {
    bom_pricing_cost = normalized.final_price;

    bom_unit_price = Number((normalized.final_price / parentQty).toFixed(2));
  } else if (level === "1") {
    bom_pricing_cost = total_bom_cost;

    bom_unit_price = Number((total_bom_cost / parentQty).toFixed(2));
  } else if (normalized.isNormalized) {
    bom_pricing_cost =
      ((bomDetails?.final_total_cost_in_pricing_currency?.cost || 0) /
        normalized.total_first_level) *
      normalized.final_price;

    bom_unit_price = bom_pricing_cost / (bomDetails.total_quantity || 1);
  } else {
    bom_pricing_cost =
      bomDetails?.final_total_cost_in_pricing_currency?.cost || 0;

    bom_unit_price = bomDetails?.unit_cost_in_pricing_currency?.cost || 0;
  }

  // Create the parent object
  const parent = {
    column_content: [
      { field: "level", content: `${level}` },
      {
        field: "item_number",
        content: bomDetails.sequence_id,
      },
      {
        field: "version",
        content: `r${bomDetails.version}`,
      },
      {
        field: "description",
        content: `${bomDetails.short_description}${
          hasNotes && bomDetails?.notes ? `\nNotes: ${bomDetails.notes}` : ""
        }`,
      },
      {
        field: "unitCost",
        content: formatCurrency(
          bom_unit_price,
          base_currency,
          useMBFormatter,
          false
        ),
      },
      {
        field: "qty",
        content: level === "1" ? parentQty : bomDetails.total_quantity || 1,
      },
      {
        field: "totalCost",
        content: formatCurrency(
          bom_pricing_cost,
          base_currency,
          useMBFormatter,
          false
        ),
      },
    ],
    id: bomDetails._id,
  };

  const assembly_process_costs =
    bomDetails?.assembly_process_costs?.map((a, idx) => {
      return {
        column_content: [
          { field: "level", content: "" },
          {
            field: "item_number",
            content: idx + 1,
          },
          {
            field: "version",
            content: `N/A`,
          },
          { field: "description", content: `${a.proc_name}` },
          {
            field: "unitCost",
            content: formatCurrency(
              a.unit_cost_in_pricing_currency?.cost || 0,
              base_currency,
              useMBFormatter,
              false
            ),
          },
          { field: "qty", content: a.quantity || 1 },
          {
            field: "totalCost",
            content: formatCurrency(
              a?.final_total_cost_in_pricing_currency?.cost || 0,
              base_currency,
              useMBFormatter,
              false
            ),
          },
        ],
        id: a.child_id,
      };
    }) || [];

  // Array to hold the immediate children of this parent
  const immediateChildren = [];

  // Process children BOMs recursively
  for (let i = 0; i < (bomDetails?.children_costs || []).length; i++) {
    const childBOM = bomDetails.children_costs[i];
    const childBOMDetails = processBOMDetailsFlatten(
      childBOM,
      `${level}.${i + 1}`,
      base_currency,
      hasNotes,
      useMBFormatter,
      normalized
    );
    immediateChildren.push(childBOMDetails[0].parent); // Add the immediate child to the array
    flattenedList.push(...childBOMDetails); // Add the entire flattened structure of the child
  }

  // Process material children (non-recursive)
  for (let i = 0; i < (bomDetails?.material_costs || []).length; i++) {
    const material = bomDetails.material_costs[i];

    let material_pricing_cost =
      material?.final_total_cost_in_pricing_currency?.cost ||
      material.total_cost?.cost ||
      0;

    let material_unit_price =
      material?.unit_cost_in_pricing_currency?.cost || 0;

    if (normalized.isNormalized) {
      material_pricing_cost =
        (material_pricing_cost / normalized.total_first_level) *
        normalized.final_price;

      material_unit_price =
        material_pricing_cost / (material.total_quantity || 1);
    }

    const materialChild = {
      parent: {
        column_content: [
          {
            field: "level",
            content: `${level}.${
              (bomDetails.children_costs?.length || 0) + i + 1
            }`,
          },
          {
            field: "item_number",
            content: material.sequence_id,
          },
          { field: "version", content: `r${material?.version}` },
          {
            field: "description",
            content: `${material?.short_description}${
              hasNotes && material?.notes ? `\nNotes: ${material.notes}` : ""
            }`,
          },
          {
            field: "unitCost",
            content: formatCurrency(
              material_unit_price,
              base_currency,
              useMBFormatter,
              false
            ),
          },
          { field: "qty", content: material.total_quantity || 1 },
          {
            field: "totalCost",
            content: formatCurrency(
              material_pricing_cost,
              base_currency,
              useMBFormatter,
              false
            ),
          },
        ],
        id: material._id,
      },
      children: [],
    };

    // Add the material child to immediateChildren and flattenedList
    immediateChildren.push(materialChild.parent);
    flattenedList.push(materialChild);
  }

  // Add the parent to the flattened list
  flattenedList.unshift({
    parent,
    assembly_process_costs,
    children: immediateChildren,
    totalParentCost:
      level === "1"
        ? total_bom_cost
        : bomDetails?.final_total_cost_in_pricing_currency?.cost || 0,
  });

  // Return the flattened list
  return flattenedList;
}

export const generateCostBreakupReport = async ({
  title,
  bomDetails,
  configState,
  ...rest
}: CostBreakupReportProps) => {
  const pricing_config = bomDetails?.pricing_config;
  const base_currency = pricing_config?.exchange_rates?.base_currency ?? "USD";

  const allLevels = configState?.levelLimit?.value === "AllLevelbreakup";
  const noBreakup = configState?.levelLimit?.value === "Nobreakup";
  const commercial = configState?.reportType?.value === "Commercial Only";
  const technical = configState?.reportType?.value === "Technical Only";
  const technoCommercial = configState?.reportType?.value === "Techno Commercial";
  const hasOtherCost = configState?.hasOtherCost;
  const hasAdjustment = configState?.hasAdjustment;
  const hasTax = configState?.hasTax;
  const hideCostGroup = configState?.onlyBOM;
  const header_color = configState?.tableHeaderColor || "#d1c4e9";
  const hasNotes = configState?.hasNotes;
  const useMBFormatter = configState?.useMBFormatter;
  const isNormalized = configState?.isNormalized ?? false;
  const hasCGI = configState?.hasCostGroupItems ?? false;

  let doc = new jsPDF({
    putOnlyUsedFonts: true,
    orientation: rest.orientation ?? "portrait",
    format: rest.paperSize,
  });

  const pageHeight = doc.internal.pageSize.getHeight(); // Get page height
  const pageWidth = doc.internal.pageSize.getWidth(); // Get page width
  const footerYPosition = pageHeight - 10; // Fixed position for footer

  // Set document properties
  doc.setProperties({ title: title });

  addHeaderTitle(
    doc,
    `Pricing Report:`,
    null,
    pricing_config?.pricing_name ?? ""
  );

  let currentY = 25;

  currentY += addTextForFrontPage(
    doc,
    `Pricing ID: ${rest.pricingId}`,
    `Calculated by: ${bomDetails.created_user}`,
    currentY
  );

  currentY += addTextForFrontPage(
    doc,
    `Root Item: ${bomDetails.calculated_cost?.sequence_id}-r${
      bomDetails.calculated_cost?.version || 0
    }`,
    `Calculated at: ${moment(bomDetails.created_time).format("ll")}`,
    currentY
  );

  currentY += addTextForFrontPage(
    doc,
    `Currency: ${base_currency}`,
    ``,
    currentY
  );

  if (pricing_config.remarks) {
    currentY += addTextForFrontPage(
      doc,
      `Remarks: ${pricing_config.remarks}`,
      ``,
      currentY
    );
  }

  currentY += 10;

  currentY += addTotalText(
    doc,
    "Total Cost (before tax)",
    formatCurrency(
      bomDetails?.gross_cost_after_discount ?? 0,
      base_currency,
      useMBFormatter
    ),
    currentY,
    header_color,
    doc.internal.pageSize.getWidth() - 8
  );

  if (!isNormalized) {
    currentY += await embedChartInJSPDF(
      doc,
      "master-chart",
      7,
      currentY,
      Math.min(160, pageWidth - 50)
    );
  }

  doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);

  if (rest.headerTitle) {
    addHeaderTitle(doc, rest.headerTitle);

    // Add a page event listener to add the header title on new pages
    // doc.internal.events.subscribe('addPage', () => addHeaderTitle(doc, rest.headerTitle));
  }

  currentY = rest.headerTitle ? 18 : 0;

  let tableEndX = 0;

  let total_first_level = 0;

  total_first_level += bomDetails?.calculated_cost?.children_costs?.reduce(
    (acc, curr) => acc + curr?.final_total_cost_in_pricing_currency?.cost || 0,
    0
  );

  total_first_level += bomDetails?.calculated_cost?.material_costs?.reduce(
    (acc, curr) => acc + curr?.final_total_cost_in_pricing_currency?.cost || 0,
    0
  );

  const result =
    processBOMDetailsFlatten(
      bomDetails?.calculated_cost,
      "1",
      base_currency,
      hasNotes,
      useMBFormatter,
      {
        isNormalized,
        final_price: bomDetails?.final_price ?? 0,
        total_first_level,
      },
      bomDetails?.pricing_config?.bom_cost_quantity,
      bomDetails?.total_bom_cost
    ) ?? [];

  if (result?.length > 0) {
    currentY += addSubTitle(
      doc,
      `L${
        result?.[0]?.parent?.column_content?.find((c) => c.field === "level")
          ?.content ?? ""
      } - ${
        bomDetails?.calculated_cost?.short_description?.split("\nNotes:")?.[0] ?? ""
      } (${bomDetails?.calculated_cost?.sequence_id ?? ""}-r${
        bomDetails?.calculated_cost?.version ?? 0
      })`,
      currentY
    );

    let costBreakupCols = [
      { header_name: "Level", field: "level", width: 80 },
      { header_name: "Item No.", field: "item_number", width: 90 },
      { header_name: "Rev.", field: "version", width: 40 },
      {
        header_name: "Description",
        field: "description",
        width: 240,
      },
      {
        header_name: `Unit Cost (${base_currency})`,
        field: "unitCost",
        width: 105,
      },
      { header_name: "Quantity", field: "qty", width: 75 },
      {
        header_name: `Price (${base_currency})`,
        field: "totalCost",
        width: 105,
      },
    ];

    if (technical || technoCommercial) {
      costBreakupCols = costBreakupCols.filter(
        (c) => c.field !== "totalCost" && c.field !== "unitCost"
      );
    }

    let parentBOMRows = [result?.[0]?.parent];

    if (technoCommercial) {
      parentBOMRows = parentBOMRows.map((c) => ({
        ...c,
        column_content: c.column_content?.filter(
          (col) => col.field !== "totalCost" && col.field !== "unitCost"
        ),
      }));
    }

    const costBreakUpTable = {
      header_color,
      columns: costBreakupCols,
      rows: parentBOMRows,
      bomDetails,
    };

    let pos = drawTable(doc, costBreakUpTable, currentY, false, rest.pricingId);

    currentY = pos.y;

    tableEndX = pos.x;

    if (!noBreakup) {
      // currentY += addSubHeader(doc, "Price Breakup", currentY, 14);

      // currentY += 2;

      if (result?.[0]?.assembly_process_costs?.length > 0 && !isNormalized) {
        currentY += addSubHeader(doc, "Overhead / Activity Breakup", currentY);

        let assemblyCols = [
          { header_name: "Level", field: "level", width: 80 },
          { header_name: "Item No.", field: "item_number", width: 90 },
          { header_name: "Rev.", field: "version", width: 40 },
          {
            header_name: "Description",
            field: "description",
            width: 240,
          },
          {
            header_name: `Unit Cost (${base_currency})`,
            field: "unitCost",
            width: 105,
          },
          { header_name: "Quantity", field: "qty", width: 75 },
          {
            header_name: `Price (${base_currency})`,
            field: "totalCost",
            width: 105,
          },
        ];

        if (technical || technoCommercial) {
          assemblyCols = assemblyCols.filter(
            (c) => c.field !== "totalCost" && c.field !== "unitCost"
          );
        }

        let assembly_process_costs = result?.[0]?.assembly_process_costs ?? [];

        if (technoCommercial) {
          assembly_process_costs = assembly_process_costs.map((c) => ({
            ...c,
            column_content: c.column_content?.filter(
              (col) => col.field !== "totalCost" && col.field !== "unitCost"
            ),
          }));
        }

        const assemblyProcessTable = {
          header_color,
          columns: assemblyCols,
          rows: assembly_process_costs,
          bomDetails,
        };

        let pos = drawTable(doc, assemblyProcessTable, currentY, true);

        currentY = pos.y;

        tableEndX = pos.x;
      }

      let costGroupCols = [
        { header_name: "Level", field: "level", width: 80 },
        { header_name: "Item No.", field: "item_number", width: 90 },
        { header_name: "Rev.", field: "version", width: 40 },
        {
          header_name: "Description",
          field: "description",
          width: 240,
        },
        {
          header_name: `Unit Cost (${base_currency})`,
          field: "unitCost",
          width: 105,
        },
        { header_name: "Quantity", field: "qty", width: 75 },
        {
          header_name: `Price (${base_currency})`,
          field: "totalCost",
          width: 105,
        },
      ];

      if (technical || technoCommercial) {
        costGroupCols = costGroupCols.filter(
          (c) => c.field !== "totalCost" && c.field !== "unitCost"
        );
      }

      let costGroupRows = [
        ...(result?.[0]?.children ?? []),
        ...(hideCostGroup
          ? []
          : pricing_config?.cost_groups?.map((c, idx) => {
              const unitCost = c?.costs?.reduce(
                (acc, c) => acc + (c?.total_cost || 0),
                0
              );
              return {
                column_content: [
                  { field: "level", content: "" },
                  { field: "item_number", content: "N/A" },
                  { field: "version", content: "N/A" },
                  { field: "description", content: c.title },
                  {
                    field: "unitCost",
                    content: formatCurrency(
                      Number(unitCost.toFixed(2)),
                      base_currency,
                      useMBFormatter,
                      false
                    ),
                  },
                  { field: "qty", content: c.quantity },
                  {
                    field: "totalCost",
                    content: formatCurrency(
                      Number((c.total_amount ?? 0).toFixed(2)),
                      base_currency,
                      useMBFormatter,
                      false
                    ),
                  },
                ],
                id: idx + 1,
              };
            }) ?? []),
      ];

      if (costGroupRows.length > 0) {
        if (technoCommercial) {
          costGroupRows = costGroupRows.map((c) => ({
            ...c,
            column_content: c.column_content?.filter(
              (col) => col.field !== "totalCost" && col.field !== "unitCost"
            ),
          }));
        }
        currentY += addSubHeader(doc, "Items Breakup", currentY);

        const costGroupTable = {
          header_color,
          columns: costGroupCols,
          rows: costGroupRows,
          bomDetails,
        };

        let pos = drawTable(doc, costGroupTable, currentY, true);

        currentY = pos.y;

        tableEndX = pos.x;
      }

      if (commercial && !isNormalized) {
        currentY += addTotalText(
          doc,
          "Total Cost",
          formatCurrency(
            (bomDetails?.total_bom_cost ?? 0) +
              (bomDetails?.total_cost_group_cost ?? 0),
            base_currency,
            useMBFormatter
          ),
          currentY,
          "",
          tableEndX
        );
      }

      if (pricing_config?.other_costs?.length > 0 && hasOtherCost) {
        currentY += addSubHeader(doc, "Other Items", currentY);

        let otherCols = [
          { header_name: "Level", field: "level", width: 80 },
          { header_name: "Item No.", field: "item_number", width: 90 },
          { header_name: "Rev.", field: "version", width: 40 },
          {
            header_name: "Description",
            field: "description",
            width: 240,
          },
          {
            header_name: `Unit Cost (${base_currency})`,
            field: "unitCost",
            width: 105,
          },
          { header_name: "Quantity", field: "qty", width: 75 },
          {
            header_name: `Price (${base_currency})`,
            field: "totalCost",
            width: 105,
          },
        ];

        if (technical || technoCommercial) {
          otherCols = otherCols.filter(
            (c) => c.field !== "totalCost" && c.field !== "unitCost"
          );
        }

        let otherCostRows =
          pricing_config?.other_costs?.map((c, idx) => {
            return {
              column_content: [
                { field: "level", content: "" },
                { field: "item_number", content: idx + 1 },
                { field: "version", content: "N/A" },
                { field: "description", content: c.description },
                {
                  field: "unitCost",
                  content: "N/A",
                },
                {
                  field: "qty",
                  content: c.type === "percentage" ? `${c.value ?? 0}%` : "",
                },
                {
                  field: "totalCost",
                  content: formatCurrency(
                    Number((c.amount ?? 0).toFixed(2)),
                    base_currency,
                    useMBFormatter,
                    false
                  ),
                },
              ],
              id: idx + 1,
            };
          }) ?? [];

        if (technoCommercial) {
          otherCostRows = otherCostRows.map((c) => ({
            ...c,
            column_content: c.column_content?.filter(
              (col) => col.field !== "totalCost" && col.field !== "unitCost"
            ),
          }));
        }

        const otherCostsTable = {
          header_color,
          columns: otherCols,
          rows: otherCostRows,
          bomDetails,
        };

        let pos = drawTable(doc, otherCostsTable, currentY, true);

        currentY = pos.y;

        tableEndX = pos.x;

        if (commercial) {
          currentY += addTotalText(
            doc,
            "Total Cost",
            formatCurrency(
              bomDetails?.gross_cost ?? 0,
              base_currency,
              useMBFormatter
            ),
            currentY,
            "",
            tableEndX
          );
        }
      }

      if (pricing_config?.discounts?.length > 0 && hasAdjustment) {
        currentY += addSubHeader(doc, "Adjustments", currentY);

        let discountCols = [
          { header_name: "Level", field: "level", width: 80 },
          { header_name: "Item No.", field: "item_number", width: 90 },
          { header_name: "Rev.", field: "version", width: 40 },
          {
            header_name: "Description",
            field: "description",
            width: 240,
          },
          {
            header_name: `Unit Cost (${base_currency})`,
            field: "unitCost",
            width: 105,
          },
          { header_name: "Quantity", field: "qty", width: 75 },
          {
            header_name: `Price (${base_currency})`,
            field: "totalCost",
            width: 105,
          },
        ];

        if (technical || technoCommercial) {
          discountCols = discountCols.filter(
            (c) => c.field !== "totalCost" && c.field !== "unitCost"
          );
        }

        let discountRows =
          pricing_config?.discounts?.map((c, idx) => {
            return {
              column_content: [
                { field: "level", content: "" },
                { field: "item_number", content: idx + 1 },
                { field: "version", content: "N/A" },
                { field: "description", content: c.description },
                {
                  field: "unitCost",
                  content: "N/A",
                },
                {
                  field: "qty",
                  content: c.type === "percentage" ? `${c.value ?? 0}%` : "",
                },
                {
                  field: "totalCost",
                  content: formatCurrency(
                    Number((c.amount ?? 0).toFixed(2)),
                    base_currency,
                    useMBFormatter,
                    false
                  ),
                },
              ],
              id: idx + 1,
            };
          }) ?? [];

        if (technoCommercial) {
          discountRows = discountRows.map((c) => ({
            ...c,
            column_content: c.column_content?.filter(
              (col) => col.field !== "totalCost" && col.field !== "unitCost"
            ),
          }));
        }

        const discountsTable = {
          header_color,
          columns: discountCols,
          rows: discountRows,
          bomDetails,
        };

        let pos = drawTable(doc, discountsTable, currentY, true);

        currentY = pos.y;

        tableEndX = pos.x;
      }
    }

    if (pricing_config?.taxes?.length > 0 && hasTax) {
      if (!technical) {
        currentY += addTotalText(
          doc,
          "Total Cost (before tax)",
          formatCurrency(
            bomDetails?.gross_cost_after_discount ?? 0,
            base_currency,
            useMBFormatter
          ),
          currentY,
          header_color,
          tableEndX
        );
      }

      currentY += addSubHeader(doc, "Taxes", currentY);

      let taxCols = [
        { header_name: "Level", field: "level", width: 80 },
        { header_name: "Item No.", field: "item_number", width: 90 },
        { header_name: "Rev.", field: "version", width: 40 },
        { header_name: "Description", field: "description", width: 240 },
        {
          header_name: `Unit Cost (${base_currency})`,
          field: "unitCost",
          width: 105,
        },
        { header_name: "Quantity", field: "qty", width: 75 },
        {
          header_name: `Price (${base_currency})`,
          field: "totalCost",
          width: 105,
        },
      ];

      if (technical || technoCommercial) {
        taxCols = taxCols.filter(
          (c) => c.field !== "totalCost" && c.field !== "unitCost"
        );
      }

      const taxesTable = {
        header_color,
        columns: taxCols,
        rows:
          pricing_config?.taxes?.map((c, idx) => {
            return {
              column_content: [
                { field: "level", content: "" },
                { field: "item_number", content: idx + 1 },
                { field: "version", content: "N/A" },
                { field: "description", content: c.description },
                {
                  field: "unitCost",
                  content: "N/A",
                },
                {
                  field: "qty",
                  content: c.type === "percentage" ? `${c.value ?? 0}%` : "",
                },
                {
                  field: "totalCost",
                  content: formatCurrency(
                    Number((c.amount ?? 0).toFixed(2)),
                    base_currency,
                    useMBFormatter,
                    false
                  ),
                },
              ],
              id: idx + 1,
            };
          }) ?? [],
        bomDetails,
      };

      let pos = drawTable(doc, taxesTable, currentY, true);

      currentY = pos.y;

      tableEndX = pos.x;
    }

    if (!technical) {
      currentY += addTotalText(
        doc,
        "Total Cost (after tax)",
        formatCurrency(
          bomDetails?.final_price ?? 0,
          base_currency,
          useMBFormatter
        ),
        currentY,
        header_color,
        tableEndX
      );
    }
  }

  if (pricing_config?.cost_groups?.length > 0 && hasCGI) {
    const cgLen = pricing_config?.cost_groups?.length ?? 0;

    currentY = 13;

    doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);

    pricing_config?.cost_groups?.forEach((cg, idx) => {
      addHeaderTitle(doc, `Cost Group - ${cg.title}`, currentY);

      currentY += 6;

      let parentCgCols = [
        { header_name: "Level", field: "level", width: 100 },
        { header_name: "Item No.", field: "item_number", width: 90 },
        { header_name: "Rev.", field: "version", width: 40 },
        { header_name: "Description", field: "description", width: 220 },
        {
          header_name: `Unit Cost (${base_currency})`,
          field: "unitCost",
          width: 105,
        },
        { header_name: "Quantity", field: "qty", width: 75 },
        {
          header_name: `Price (${base_currency})`,
          field: "totalCost",
          width: 105,
        },
      ];

      if (technical || technoCommercial) {
        parentCgCols = parentCgCols.filter(
          (c) => c.field !== "totalCost" && c.field !== "unitCost"
        );
      }

      const unitCost = cg?.costs?.reduce(
        (acc, c) => acc + (c?.total_cost || 0),
        0
      );

      const parentCgTable = {
        header_color,
        columns: parentCgCols,
        rows: [
          {
            column_content: [
              { field: "level", content: "" },
              { field: "item_number", content: "N/A" },
              { field: "version", content: "N/A" },
              { field: "description", content: cg.title },
              {
                field: "unitCost",
                content: formatCurrency(
                  Number(unitCost.toFixed(2)),
                  base_currency,
                  useMBFormatter,
                  false
                ),
              },
              { field: "qty", content: cg.quantity },
              {
                field: "totalCost",
                content: formatCurrency(
                  Number((cg.total_amount ?? 0).toFixed(2)),
                  base_currency,
                  useMBFormatter,
                  false
                ),
              },
            ],
            id: idx + 1,
          },
        ],
        bomDetails,
      };

      let pos = drawTable(doc, parentCgTable, currentY, false);

      currentY = pos.y;

      tableEndX = pos.x;

      if (cg.costs?.length > 0) {
        if (
          currentY >= doc.internal.pageSize.getHeight() - 50 &&
          idx < cgLen - 1
        ) {
          currentY = 13;

          doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
        }

        currentY += addSubHeader(doc, "Items Breakup", currentY);

        let cgCostCols = [
          { header_name: "Level", field: "level", width: 100 },
          { header_name: "Item No.", field: "item_number", width: 90 },
          { header_name: "Rev.", field: "version", width: 40 },
          { header_name: "Description", field: "description", width: 220 },
          {
            header_name: `Unit Cost (${base_currency})`,
            field: "unitCost",
            width: 105,
          },
          { header_name: "Quantity", field: "qty", width: 75 },
          {
            header_name: `Price (${base_currency})`,
            field: "totalCost",
            width: 105,
          },
        ];

        if (technical || technoCommercial) {
          cgCostCols = cgCostCols.filter(
            (c) => c.field !== "totalCost" && c.field !== "unitCost"
          );
        }

        const cgCostTable = {
          header_color,
          columns: cgCostCols,
          rows: cg.costs?.map((c, idx) => {
            return {
              column_content: [
                { field: "level", content: "" },
                { field: "item_number", content: "N/A" },
                { field: "version", content: "N/A" },
                { field: "description", content: c.description },
                {
                  field: "unitCost",
                  content: formatCurrency(
                    Number(c.value.toFixed(2)),
                    base_currency,
                    useMBFormatter,
                    false
                  ),
                },
                { field: "qty", content: c.quantity },
                {
                  field: "totalCost",
                  content: formatCurrency(
                    Number((c.total_cost ?? 0).toFixed(2)),
                    base_currency,
                    useMBFormatter,
                    false
                  ),
                },
              ],
              id: idx + 1,
            };
          }),
          bomDetails,
        };

        let pos = drawTable(doc, cgCostTable, currentY, true);

        currentY = pos.y;

        tableEndX = pos.x;
      }

      // if (commercial) {
      //   if (
      //     currentY >= doc.internal.pageSize.getHeight() - 50 &&
      //     idx < cgLen - 1
      //   ) {
      //     currentY = 13;

      //     doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
      //   }

      //   currentY += addTotalText(
      //     doc,
      //     "Total Cost",
      //     formatCurrency(r.totalParentCost, base_currency, useMBFormatter),
      //     currentY,
      //     "",
      //     tableEndX
      //   );
      // }

      currentY += 20;

      if (
        currentY >= doc.internal.pageSize.getHeight() - 50 &&
        idx < cgLen - 1
      ) {
        currentY = 13;

        doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
      }
    });
  }

  if (allLevels) {
    const childLevels =
      result
        ?.slice(1)
        ?.filter((c) =>
          c.parent?.column_content
            ?.find((c) => c.field === "item_number")
            ?.content?.includes("BOM")
        ) ?? [];

    if (childLevels?.length > 0) {
      if (bomDetails?.pricing_config?.cost_groups?.length <= 0 || !hasCGI) {
        currentY = 13;
        doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
      }
    }

    childLevels?.forEach((r, idx) => {
      if (
        currentY >= doc.internal.pageSize.getHeight() - 50 &&
        idx < childLevels.length - 1
      ) {
        currentY = 13;

        doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
      }

      addHeaderTitle(doc, "Details Sheet", currentY);
      currentY += 6;
      currentY += addSubTitle(
        doc,
        `L${
          r.parent?.column_content?.find((c) => c.field === "level")?.content ??
          ""
        } - ${
          r.parent?.column_content
            ?.find((c) => c.field === "description")
            ?.content?.split("\nNotes:")?.[0] ?? ""
        } (${
          r.parent?.column_content?.find((c) => c.field === "item_number")
            ?.content ?? ""
        }-${
          r.parent?.column_content?.find((c) => c.field === "version")
            ?.content ?? 0
        })`,
        currentY
      );

      let parentCols = [
        { header_name: "Level", field: "level", width: 100 },
        { header_name: "Item No.", field: "item_number", width: 90 },
        { header_name: "Rev.", field: "version", width: 40 },
        { header_name: "Description", field: "description", width: 220 },
        {
          header_name: `Unit Cost (${base_currency})`,
          field: "unitCost",
          width: 105,
        },
        { header_name: "Quantity", field: "qty", width: 75 },
        {
          header_name: `Price (${base_currency})`,
          field: "totalCost",
          width: 105,
        },
      ];

      if (technical || technoCommercial) {
        parentCols = parentCols.filter(
          (c) => c.field !== "totalCost" && c.field !== "unitCost"
        );
      }

      const parentTable = {
        header_color,
        columns: parentCols,
        rows: [r?.parent],
        bomDetails,
      };

      let pos = drawTable(doc, parentTable, currentY, false);

      currentY = pos.y;

      tableEndX = pos.x;

      if (r.assembly_process_costs?.length > 0) {
        if (
          currentY >= doc.internal.pageSize.getHeight() - 50 &&
          idx < childLevels.length - 1
        ) {
          currentY = 13;

          doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
        }

        currentY += addSubHeader(doc, "Overhead / Activity Breakup", currentY);

        let assemblyCols = [
          { header_name: "Level", field: "level", width: 100 },
          { header_name: "Item No.", field: "item_number", width: 90 },
          { header_name: "Rev.", field: "version", width: 40 },
          { header_name: "Description", field: "description", width: 220 },
          {
            header_name: `Unit Cost (${base_currency})`,
            field: "unitCost",
            width: 105,
          },
          { header_name: "Quantity", field: "qty", width: 75 },
          {
            header_name: `Price (${base_currency})`,
            field: "totalCost",
            width: 105,
          },
        ];

        if (technical || technoCommercial) {
          assemblyCols = assemblyCols.filter(
            (c) => c.field !== "totalCost" && c.field !== "unitCost"
          );
        }

        const assemblyProcessTable = {
          header_color,
          columns: assemblyCols,
          rows: r.assembly_process_costs,
          bomDetails,
        };

        let pos = drawTable(doc, assemblyProcessTable, currentY, true);

        currentY = pos.y;

        tableEndX = pos.x;
      }

      if (r.children?.length > 0) {
        if (
          currentY >= doc.internal.pageSize.getHeight() - 50 &&
          idx < childLevels.length - 1
        ) {
          currentY = 13;

          doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
        }

        currentY += addSubHeader(doc, "Items Breakup", currentY);

        let treeCols = [
          { header_name: "Level", field: "level", width: 100 },
          { header_name: "Item No.", field: "item_number", width: 90 },
          { header_name: "Rev.", field: "version", width: 40 },
          { header_name: "Description", field: "description", width: 220 },
          {
            header_name: `Unit Cost (${base_currency})`,
            field: "unitCost",
            width: 105,
          },
          { header_name: "Quantity", field: "qty", width: 75 },
          {
            header_name: `Price (${base_currency})`,
            field: "totalCost",
            width: 105,
          },
        ];

        if (technical || technoCommercial) {
          treeCols = treeCols.filter(
            (c) => c.field !== "totalCost" && c.field !== "unitCost"
          );
        }

        const treeTable = {
          header_color,
          columns: treeCols,
          rows: r.children,
          bomDetails,
        };

        let pos = drawTable(doc, treeTable, currentY, true);

        currentY = pos.y;

        tableEndX = pos.x;
      }

      if (commercial) {
        if (
          currentY >= doc.internal.pageSize.getHeight() - 50 &&
          idx < childLevels.length - 1
        ) {
          currentY = 13;

          doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
        }

        currentY += addTotalText(
          doc,
          "Total Cost",
          formatCurrency(r.totalParentCost, base_currency, useMBFormatter),
          currentY,
          "",
          tableEndX
        );
      }

      currentY += 20;

      if (
        currentY >= doc.internal.pageSize.getHeight() - 50 &&
        idx < childLevels.length - 1
      ) {
        currentY = 13;

        doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
      }
    });
  }

  // if (pricing_config?.cost_groups?.length > 0) {
  //   pricing_config?.cost_groups?.forEach((cg) => {
  //     if (cg.costs?.length > 0) {
  //       currentY += addSubTitle(doc, `${cg.title} (Cost Group Item)`, currentY);

  //       const costGroupItemsTable = {
  //         header_color: "#d1c4e9",
  //         columns: [
  //           { header_name: "Description", field: "description", width: 250 },
  //           { header_name: "Units", field: "units", width: 150 },
  //           {
  //             header_name: "Value",
  //             field: "value",
  //             width: 170,
  //           },
  //           { header_name: "Quantity", field: "quantity", width: 120 },
  //           {
  //             header_name: `Total Amount (${base_currency})`,
  //             field: "total_cost",
  //             width: 150,
  //           },
  //         ],
  //         rows:
  //           cg?.costs?.map((c, idx) => {
  //             return {
  //               column_content: [
  //                 { field: "description", content: c.description },
  //                 { field: "units", content: c.units },
  //                 {
  //                   field: "value",
  //                   content: c.value ?? 0,
  //                 },
  //                 { field: "quantity", content: c.quantity },
  //                 {
  //                   field: "total_cost",
  //                   content: Number((c.total_cost ?? 0).toFixed(2)),
  //                 },
  //               ],
  //               id: idx + 1,
  //             };
  //           }) ?? [],
  //         bomDetails,
  //       };

  //       currentY = drawTable(doc, costGroupItemsTable, currentY, true);
  //     }
  //   });
  // }

  if (rest.showFooter) {
    // Now ensure consistent footer on all pages
    const totalPages = doc.getNumberOfPages(); // Get total number of pages

    // Loop through all pages to add footer at the same Y position
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i); // Set focus on page 'i'

      // Footer logic
      const footerText = `Generated using IEHUB on ${moment(new Date()).format(
        "ll"
      )}`;
      doc.setFontSize(7);
      doc.setTextColor(125, 125, 125); // Gray text color
      const textWidth = doc.getTextWidth(footerText);

      // Draw footer at the fixed bottom-right of each page
      if (rest.footerURL) {
        doc.textWithLink(
          footerText,
          pageWidth - textWidth - 10,
          footerYPosition,
          { url: rest.footerURL }
        );
      } else {
        doc.text(footerText, pageWidth - textWidth - 10, footerYPosition);
      }
    }
  }

  return doc.output("datauristring"); // base64 string
};
